import moment from "moment";
import type { TeachrCourseSessionInfo } from "../../../Repositories/PunctualCourseRepository";
import {
    MAX_SESSION_DURATION_HOURS,
    SessionRow,
    sessionEnd,
    sessionRangesOverlap,
    sessionStart,
} from "../../DeclarationCourse/SessionList";
import { clock, dayLabel } from "../format";
import { vocab } from "../vocabulary";

/**
 * Règles du planning (étape 2) du nouveau parcours, copiées de l'ancien
 * (SecondStep, SessionList, sessionConflicts) et exprimées par séance pour
 * afficher l'erreur sur la carte fautive. Helpers purs, testés dans
 * src/Test/DeclarationV2/planningRules.test.ts.
 */

export interface TimeSlot {
    /** « 16:00 » */
    value: string;
    /** « 16h00 » */
    label: string;
}

/** Créneaux de la journée par pas de 15 minutes (06:00 à 23:45). */
export const timeSlots = (stepMinutes = 15, fromHour = 6, toHour = 23): TimeSlot[] => {
    const slots: TimeSlot[] = [];
    for (let h = fromHour; h <= toHour; h++) {
        for (let m = 0; m < 60; m += stepMinutes) {
            const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
            slots.push({ value, label: value.replace(":", "h") });
        }
    }
    return slots;
};

/** « HH:mm » d'un ISO datetime, ou null. */
export const timeValue = (iso: string | null | undefined): string | null => (iso ? moment(iso).format("HH:mm") : null);

/** Applique « HH:mm » sur un jour (ou aujourd'hui) et renvoie l'ISO datetime. */
export const timeToIso = (time: string, date?: string | null): string => {
    const [h, m] = time.split(":").map((part) => parseInt(part, 10));
    return moment(date ?? undefined).set({ hour: h, minute: m, second: 0, millisecond: 0 }).toISOString();
};

/** Durée arrondie au quart d'heure, entre 15 minutes et le maximum autorisé. */
export const clampDuration = (hours: number): number => {
    const quarter = Math.round(hours * 4) / 4;
    return Math.min(MAX_SESSION_DURATION_HOURS, Math.max(0.25, quarter));
};

/**
 * Seuil des 2 h 30 après la validation du compte d'avance immédiate, arrondi
 * aux 5 minutes supérieures (validation à 11h17 : pas de séance avant 13h50).
 */
export const activationThreshold = (activationDate: string | null | undefined): moment.Moment | null => {
    if (!activationDate) return null;
    const activation = moment(activationDate);
    if (!activation.isValid()) return null;
    const threshold = activation.add(2.5, "hours").startOf("minute");
    const remainder = threshold.minute() % 5;
    return remainder === 0 ? threshold : threshold.add(5 - remainder, "minute");
};

/** Heure de début minimale (« HH:mm ») le jour de la validation du compte, null les autres jours. */
export const earliestStartOn = (date: string | null | undefined, activationDate: string | null | undefined): string | null => {
    const threshold = activationThreshold(activationDate);
    if (!date || !threshold || !threshold.isSame(moment(date), "day")) return null;
    return threshold.format("HH:mm");
};

/**
 * Le jour de la validation du compte, une séance qui commencerait trop tôt est
 * décalée à l'heure minimale (13h47 arrondi à 13h50), plutôt que refusée.
 */
export const enforceEarliestStart = (rows: SessionRow[], activationDate: string | null | undefined): SessionRow[] => {
    const threshold = activationThreshold(activationDate);
    if (!threshold) return rows;
    let changed = false;
    const next = rows.map((row) => {
        const start = sessionStart(row);
        if (!row.date || !start || !start.isSame(threshold, "day") || !start.isBefore(threshold)) return row;
        changed = true;
        return { ...row, startTime: timeToIso(threshold.format("HH:mm"), row.date) };
    });
    return changed ? next : rows;
};

/** Premier jour sélectionnable : validation du compte, sinon début du mois en cours. */
export const firstSelectableDay = (activationDate: string | null | undefined): string => {
    const monthStart = moment().startOf("month");
    const activation = activationDate ? moment(activationDate) : null;
    const from = activation && activation.isValid() && activation.isAfter(monthStart) ? activation : monthStart;
    return from.format("YYYY-MM-DD");
};

/** Séance existante du prof qui chevauche le créneau donné, ou null. */
export const findExistingConflict = (
    start: moment.Moment,
    end: moment.Moment,
    existing: TeachrCourseSessionInfo[],
    excludeCourseId?: number | null
): TeachrCourseSessionInfo | null => {
    for (const session of existing) {
        if (excludeCourseId && session.courseId === excludeCourseId) continue;
        if (sessionRangesOverlap(start, end, moment(session.date), moment(session.endDate))) return session;
    }
    return null;
};

/** Vrai si la séance ne se termine pas le même jour : finir à 00h00, c'est déjà le lendemain. */
const endsAfterMidnight = (start: moment.Moment, end: moment.Moment): boolean => !end.isSame(start, "day");

/** Heure de fin (« HH:mm ») si la séance déborde sur le lendemain, sinon null. */
export const endsNextDay = (time: string | null, durationHours: number): string | null => {
    if (!time) return null;
    const start = moment(timeToIso(time, "2000-01-01"));
    const end = start.clone().add(durationHours, "hours");
    return endsAfterMidnight(start, end) ? end.format("HH:mm") : null;
};

/** Conflit d'une date cochée du calendrier avec l'heure et la durée par défaut. */
export const dateConflict = (
    date: string,
    time: string | null,
    durationHours: number,
    existing: TeachrCourseSessionInfo[]
): TeachrCourseSessionInfo | null => {
    if (!time) return null;
    const start = moment(timeToIso(time, date));
    const end = start.clone().add(durationHours, "hours");
    return findExistingConflict(start, end, existing);
};

/** Arrondi au quart d'heure supérieur (17h05 → 17h15). */
export const roundUpToQuarter = (m: moment.Moment): moment.Moment => {
    const rounded = m.clone().startOf("minute");
    const remainder = rounded.minute() % 15;
    return remainder === 0 ? rounded : rounded.add(15 - remainder, "minute");
};

export interface FreeSlot {
    /** « 17:00 » */
    time: string;
    /** Cours existant après lequel la séance est placée. */
    after: TeachrCourseSessionInfo;
}

/**
 * Quand le créneau voulu chevauche un cours existant, on garde la date et on
 * repousse le début à la fin de ce cours, arrondie au quart d'heure, autant de
 * fois que nécessaire. Null s'il n'y a pas de chevauchement, ou si la séance
 * décalée ne tiendrait plus avant minuit : le chevauchement reste alors signalé.
 */
export const nextFreeStart = (
    date: string,
    time: string,
    durationHours: number,
    existing: TeachrCourseSessionInfo[],
    excludeCourseId?: number | null
): FreeSlot | null => {
    let start = moment(timeToIso(time, date));
    let end = start.clone().add(durationHours, "hours");
    let conflict = findExistingConflict(start, end, existing, excludeCourseId);
    if (!conflict) return null;
    let after = conflict;
    for (let i = 0; i < 24 && conflict; i++) {
        after = conflict;
        start = roundUpToQuarter(moment(conflict.endDate));
        if (!start.isSame(moment(date), "day")) return null;
        end = start.clone().add(durationHours, "hours");
        if (endsAfterMidnight(start, end)) return null;
        conflict = findExistingConflict(start, end, existing, excludeCourseId);
    }
    if (conflict) return null;
    return { time: start.format("HH:mm"), after };
};

export interface ShiftedRow {
    key: string;
    date: string;
    /** « 16:00 » voulu, « 17:00 » retenu. */
    from: string;
    to: string;
    after: TeachrCourseSessionInfo;
}

/** Séances générées qui chevauchent un cours existant : début décalé au prochain créneau libre du jour. */
export const shiftRowsAfterConflicts = (rows: SessionRow[], existing: TeachrCourseSessionInfo[]): { rows: SessionRow[]; shifted: ShiftedRow[] } => {
    const shifted: ShiftedRow[] = [];
    const next = rows.map((row) => {
        const time = timeValue(row.startTime);
        if (!row.date || !time) return row;
        const free = nextFreeStart(row.date, time, row.duration, existing);
        if (!free) return row;
        shifted.push({ key: row.key, date: row.date, from: time, to: free.time, after: free.after });
        return { ...row, startTime: timeToIso(free.time, row.date) };
    });
    return { rows: shifted.length > 0 ? next : rows, shifted };
};

/** « Début décalé à 17h00, après votre cours avec Marie D. (15h30 à 17h00). » */
export const describeShift = (to: string, after: TeachrCourseSessionInfo): string =>
    `Début décalé à ${to.replace(":", "h")}, après ${vocab.theCourse()} que vous avez déjà avec ${after.studentName} (${clock(after.date)} à ${clock(after.endDate)}).`;

/** « Vous avez déjà un cours avec Marie D. de 15h30 à 17h00 ce jour-là. » */
export const describeExistingConflict = (session: TeachrCourseSessionInfo): string =>
    `Vous avez déjà ${vocab.aCourse()} avec ${session.studentName} de ${clock(session.date)} à ${clock(session.endDate)} ce jour-là.`;

/** « Décalez le début à 17h00 ou supprimez la séance. » */
export const suggestAfterConflict = (session: TeachrCourseSessionInfo): string =>
    `Décalez le début à ${clock(session.endDate)} ou plus tard, ou supprimez la séance.`;

export interface RowIssueContext {
    activationDate: string | null | undefined;
    existing: TeachrCourseSessionInfo[];
    /** Mois de référence (YYYY-MM) : celui de la majorité des séances, sinon le mois en cours. */
    referenceMonth?: string | null;
}

export interface RowIssue {
    key: string;
    message: string;
}

/** Mois majoritaire des séances datées (YYYY-MM), ou null. */
export const majorityMonth = (rows: SessionRow[]): string | null => {
    const counts = new Map<string, number>();
    rows.forEach((row) => {
        const start = sessionStart(row);
        if (start) counts.set(start.format("YYYY-MM"), (counts.get(start.format("YYYY-MM")) ?? 0) + 1);
    });
    const best = [...counts.entries()].sort((a, b) => b[1] - a[1])[0];
    return best ? best[0] : null;
};

/**
 * Erreur propre à une séance, dans l'ordre : incomplète, durée, date avant la
 * validation du compte ou moins de 2 h 30 après, mois passé, mois différent des
 * autres séances, chevauchement avec une autre séance de la déclaration,
 * chevauchement avec un autre cours du prof.
 */
export const rowIssues = (rows: SessionRow[], context: RowIssueContext): RowIssue[] => {
    const issues: RowIssue[] = [];
    const threshold = activationThreshold(context.activationDate);
    const activation = context.activationDate ? moment(context.activationDate).startOf("day") : null;
    const currentMonth = moment().format("YYYY-MM");
    const reference = context.referenceMonth ?? majorityMonth(rows) ?? currentMonth;

    rows.forEach((row, index) => {
        const start = sessionStart(row);
        const end = sessionEnd(row);
        const push = (message: string) => issues.push({ key: row.key, message });
        if (!row.date) return push("Choisissez le jour de cette séance.");
        if (!row.startTime || !start || !end) return push("Choisissez l'heure de début de cette séance.");
        if (row.duration < 0.25 || row.duration > MAX_SESSION_DURATION_HOURS) {
            return push(`La durée doit être comprise entre 15 minutes et ${MAX_SESSION_DURATION_HOURS} heures.`);
        }
        if (endsAfterMidnight(start, end)) {
            return push(`Cette séance se terminerait le lendemain à ${clock(end)} : réduisez la durée ou avancez l'heure de début.`);
        }
        if (activation && start.isBefore(activation)) {
            return push(`Le compte d'avance immédiate ${vocab.ofTheCourse() === "du cours" ? "de l'" + vocab.client() : "du " + vocab.client()} a été validé le ${activation.format("DD/MM/YYYY")} : aucune séance avant cette date.`);
        }
        if (threshold && start.isBefore(threshold)) {
            return push(`Le compte a été validé le ${moment(context.activationDate).format("DD/MM/YYYY")} à ${clock(moment(context.activationDate))} : la séance ne peut pas commencer avant ${clock(threshold)}.`);
        }
        if (start.format("YYYY-MM") < currentMonth) {
            return push("Ce mois est déjà passé : choisissez une date du mois en cours.");
        }
        if (start.format("YYYY-MM") !== reference) {
            return push(`Toutes les séances d'une déclaration sont dans le même mois. Les autres sont en ${moment(reference, "YYYY-MM").locale("fr").format("MMMM")}.`);
        }
        for (let j = 0; j < rows.length; j++) {
            if (j === index) continue;
            const otherStart = sessionStart(rows[j]);
            const otherEnd = sessionEnd(rows[j]);
            if (otherStart && otherEnd && sessionRangesOverlap(start, end, otherStart, otherEnd)) {
                return push(`Cette séance chevauche la séance du ${dayLabel(otherStart).toLowerCase()} de ${clock(otherStart)} à ${clock(otherEnd)}.`);
            }
        }
        const conflict = findExistingConflict(start, end, context.existing);
        if (conflict) {
            return push(`Cette séance chevauche ${vocab.theCourse()} déjà déclaré avec ${conflict.studentName} de ${clock(conflict.date)} à ${clock(conflict.endDate)}. ${suggestAfterConflict(conflict)}`);
        }
        return undefined;
    });
    return issues;
};

/** Toutes les séances complètes sont terminées : déclaration d'un cours déjà passé. */
export const allSessionsOver = (rows: SessionRow[], now: moment.Moment = moment()): boolean => {
    const ends = rows.map(sessionEnd).filter(Boolean) as moment.Moment[];
    return ends.length > 0 && ends.every((end) => end.isBefore(now));
};

/** Bornes (YYYY-MM-DD) d'un mois, élargies d'un jour, pour charger les séances existantes. */
export const monthRange = (month: moment.Moment): { from: string; to: string } => ({
    from: month.clone().startOf("month").subtract(1, "day").format("YYYY-MM-DD"),
    to: month.clone().endOf("month").add(1, "day").format("YYYY-MM-DD"),
});
